// src/pages/Public/ForgotPassword.jsx
import React, { useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Alert,
  Spinner,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import { FaPhone } from "react-icons/fa";
import axios from "../../api/axios";

const ForgotPassword = () => {
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation du téléphone
    const phoneClean = phone.replace(/\s+/g, "").replace(/[^0-9+]/g, "");
    if (!phoneClean || phoneClean.length < 8) {
      setError(
        "Veuillez entrer un numéro de téléphone valide (minimum 8 chiffres)",
      );
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      await axios.post("/auth/forgot-password/", {
        identifier: phoneClean,
      });

      // Masquer partiellement le numéro pour l'affichage
      const phoneMasked =
        phoneClean.length > 6
          ? phoneClean.slice(0, 4) + "****" + phoneClean.slice(-2)
          : phoneClean;

      setSuccess(
        `✅ Un lien de réinitialisation a été envoyé sur votre numéro ${phoneMasked} via WhatsApp.`,
      );
      setPhone("");
    } catch (err) {
      const errorMsg =
        err.response?.data?.error ||
        err.response?.data?.message ||
        "Une erreur est survenue. Veuillez réessayer.";
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Formater le numéro de téléphone pendant la saisie
  const handlePhoneChange = (e) => {
    let value = e.target.value;
    // Autoriser uniquement les chiffres et le +
    value = value.replace(/[^0-9+]/g, "");

    // Limiter la longueur
    if (value.length > 20) return;

    setPhone(value);
  };

  return (
    <div className="forgot-password-page">
      <Container>
        <Row className="justify-content-center align-items-center min-vh-100">
          <Col lg={4} md={6} sm={8} xs={11}>
            <Card className="shadow-lg border-0 rounded-4">
              <Card.Body className="p-4">
                <div className="text-center mb-4">
                  <div className="mb-3" style={{ fontSize: "48px" }}>
                    📱
                  </div>
                  <h3 className="fw-bold" style={{ color: "#C62828" }}>
                    Mot de passe oublié ?
                  </h3>
                  <p className="text-muted">
                    Entrez votre numéro de téléphone pour recevoir un lien de
                    réinitialisation par WhatsApp.
                  </p>
                </div>

                {error && (
                  <Alert variant="danger" className="rounded-3">
                    <div className="d-flex align-items-start">
                      <span style={{ fontSize: "18px", marginRight: "10px" }}>
                        ❌
                      </span>
                      <div>{error}</div>
                    </div>
                  </Alert>
                )}

                {success && (
                  <Alert variant="success" className="rounded-3">
                    <div className="d-flex align-items-start">
                      <span style={{ fontSize: "20px", marginRight: "12px" }}>
                        ✅
                      </span>
                      <div>
                        <strong>Lien envoyé !</strong>
                        <p className="mb-0 small">{success}</p>
                        <p className="mb-0 mt-1 small text-muted">
                          📌 Vérifiez vos messages WhatsApp
                        </p>
                      </div>
                    </div>
                  </Alert>
                )}

                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold">
                      📱 Numéro de téléphone
                    </Form.Label>
                    <div className="input-wrapper position-relative">
                      <FaPhone className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" />
                      <Form.Control
                        type="tel"
                        value={phone}
                        onChange={handlePhoneChange}
                        placeholder="+223 61234567"
                        className="ps-5 py-2 rounded-3"
                        required
                        disabled={loading}
                      />
                    </div>
                    <Form.Text className="text-muted">
                      Format: +237XXXXXXXX
                    </Form.Text>
                  </Form.Group>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-100 py-2 rounded-3"
                    style={{
                      backgroundColor: "#C62828",
                      borderColor: "#C62828",
                    }}
                  >
                    {loading ? (
                      <>
                        <Spinner
                          animation="border"
                          size="sm"
                          className="me-2"
                        />
                        Envoi en cours...
                      </>
                    ) : (
                      "📤 Envoyer le lien WhatsApp"
                    )}
                  </Button>

                  <div className="text-center mt-3">
                    <Link to="/login" className="text-decoration-none">
                      ← Retour à la connexion
                    </Link>
                  </div>
                </Form>

                <hr className="my-4" />
                <div className="text-center">
                  <div className="d-flex justify-content-center gap-3">
                    <span>💬</span>
                    <span className="text-muted small">
                      Le lien de réinitialisation vous sera envoyé par{" "}
                      <strong>WhatsApp</strong>
                    </span>
                    <span>📱</span>
                  </div>
                  <div className="mt-2">
                    <span className="badge bg-success">
                      ✓ WhatsApp sécurisé
                    </span>
                    <span className="badge bg-secondary ms-2">
                      ✓ 24h de validité
                    </span>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      <style>{`
        .forgot-password-page {
          background: linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%);
          min-height: 100vh;
          display: flex;
          align-items: center;
        }
        
        .input-wrapper {
          position: relative;
        }
        
        .input-wrapper .form-control:focus {
          border-color: #C62828;
          box-shadow: 0 0 0 0.2rem rgba(198, 40, 40, 0.25);
        }
        
        .input-wrapper .form-control {
          padding-left: 45px !important;
        }
        
        .btn-link {
          color: #C62828;
          font-size: 13px;
        }
        
        .btn-link:hover {
          color: #8B0000;
        }
        
        .badge {
          font-weight: 500;
          padding: 6px 12px;
        }
        
        @media (max-width: 576px) {
          .card-body {
            padding: 20px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default ForgotPassword;
